import { Calendar, Check, ChevronRight, History, MapPin, Package, Plus, Search, X } from 'lucide-react';
import { DEFAULT_FILTERS } from '../../components/OrderFilters';
import { formatAttemptTime } from '../../components/ContactHistorySheet';
import EmptyStateCard from '../../components/EmptyStateCard';
import { SkeletonRow } from '../../components/Skeleton';
import { formatOrderTime as formatOrderDate } from '../../utils/orderTime';
import { orderBarColor } from '../../constants/deliveryStatus';

export default function MerchantCommandesView({
  loading,
  deliveries,
  bilanJour,
  confTab,
  confCounts,
  merchantFilters,
  showFilterSheet,
  filteredMerchantOrders,
  missingPhoneCount,
  missingAmountCount,
  hasActiveMerchantFilters,
  statusConfig,
  selectedId,
  setConfTab,
  setMerchantFilters,
  setShowFilterSheet,
  onCreateOrder,
  onSelectOrder,
  onOpenHistory,
}) {
  return (
    <>
      <div style={{ marginBottom: '14px' }}>
        {/* En-tête de la journée. Affiché même à zéro, sinon l'écran s'ouvre
            sur un vide le matin ; le détail par statut vit dans les tuiles
            juste en dessous, inutile de le répéter ici. */}
        {!loading && (
          <div style={{
            background: 'linear-gradient(135deg, #07162d 0%, #173b78 100%)',
            borderRadius: '18px', padding: '20px', marginBottom: '12px',
            boxShadow: '0 8px 24px rgba(10,22,40,0.18)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1.6px', fontWeight: 700 }}>
                  Chiffre d'affaires du jour
                </div>
                <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '34px', fontWeight: 800, color: '#ffffff', lineHeight: 1.05, marginTop: '4px' }}>
                  {bilanJour.ca.toLocaleString('fr-FR')}
                  <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.45)', marginLeft: '5px' }}>FCFA</span>
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1.2px', fontWeight: 700 }}>
                  Net à recevoir
                </div>
                <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '22px', fontWeight: 800, color: '#4ade80', lineHeight: 1.1, marginTop: '4px' }}>
                  {bilanJour.net.toLocaleString('fr-FR')} <span style={{ fontSize: '11px', opacity: 0.6 }}>F</span>
                </div>
                {bilanJour.frais > 0 && (
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                    après {bilanJour.frais.toLocaleString('fr-FR')} F de frais
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Les compteurs des onglets inactifs étaient en gris très pâle : le
            marchand ne voyait pas ses rappels en attente ni ses annulations.
            Tous restent lisibles, seul l'onglet actif se distingue. */}
        <div className="stat-tiles">
          {[
            { id: 'new',       label: 'Attente',  accent: '#c2410c', img: '/images/stat-commandes-a-traiter.webp',  alt: 'Commandes en attente' },
            { id: 'recall',    label: 'Rappel',   accent: '#173b78', img: '/images/stat-rappel.webp',               alt: 'Clients à rappeler' },
            { id: 'confirmed', label: 'Confirmé', accent: '#16a34a', img: '/images/stat-commandes-confirmees.webp', alt: 'Commandes confirmées' },
            { id: 'cancelled', label: 'Annulé', alert: true,   accent: '#c53a2d', img: '/images/stat-commandes-annulees.webp',   alt: 'Commandes annulées' },
          ].map((tab) => {
            const active = confTab === tab.id;
            const count = confCounts[tab.id];
            return (
              <button
                key={tab.id}
                type="button"
                className={`stat-tile${active ? ' is-active' : ''}`}
                onClick={() => setConfTab(tab.id)}
                style={{
                  borderColor: active ? tab.accent : undefined,
                  boxShadow: active ? `0 4px 16px ${tab.accent}26` : undefined,
                }}
              >
                <span className={`stat-tile-bar${tab.alert ? ' stat-tile-bar--alert' : ''}`} />
                <img className="stat-tile-img" src={tab.img} alt={tab.alt} loading="lazy" width="44" height="44" />
                <span className="stat-tile-text">
                  <span className="stat-tile-value" style={{ color: count > 0 ? tab.accent : '#94a3b8' }}>
                    {loading ? '—' : count}
                  </span>
                  <span className="stat-tile-label">{tab.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!loading && deliveries.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <input
                value={merchantFilters.search}
                onChange={(e) => setMerchantFilters((filters) => ({ ...filters, search: e.target.value }))}
                placeholder="Nom, adresse, produit..."
                style={{ width: '100%', padding: '10px 34px 10px 34px', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', background: 'white', color: '#1a1a2e' }}
                onFocus={(e) => { e.target.style.borderColor = '#173b78'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
              />
              {merchantFilters.search && (
                <button
                  onClick={() => setMerchantFilters((filters) => ({ ...filters, search: '' }))}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '3px', borderRadius: '6px', display: 'flex' }}
                >
                  <X size={13} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilterSheet(true)}
              style={{ flexShrink: 0, width: '42px', height: '42px', borderRadius: '12px', border: `1.5px solid ${merchantFilters.period && merchantFilters.period !== 'all' ? '#173b78' : '#e2e8f0'}`, background: merchantFilters.period && merchantFilters.period !== 'all' ? '#f0f4ff' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transition: 'all 0.15s' }}
            >
              <Calendar size={16} color={merchantFilters.period && merchantFilters.period !== 'all' ? '#173b78' : '#94a3b8'} />
              {merchantFilters.period && merchantFilters.period !== 'all' && (
                <div style={{ position: 'absolute', top: '6px', right: '6px', width: '7px', height: '7px', borderRadius: '50%', background: '#c53a2d' }} />
              )}
            </button>
          </div>

          <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
              {filteredMerchantOrders.length} résultat{filteredMerchantOrders.length > 1 ? 's' : ''}
              {merchantFilters.period && merchantFilters.period !== 'all' && (
                <span style={{ marginLeft: '6px', color: '#173b78', fontWeight: 700 }}>
                  · {{ today: "aujourd'hui", week: 'semaine', month: 'mois', custom: 'période' }[merchantFilters.period] || merchantFilters.period}
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {missingPhoneCount > 0 && (
                <span style={{ padding: '3px 8px', borderRadius: '999px', border: '1px solid #fecdd3', background: '#fff1f2', color: '#be123c', fontSize: '10px', fontWeight: 700 }}>
                  {missingPhoneCount} sans tél.
                </span>
              )}
              {missingAmountCount > 0 && (
                <span style={{ padding: '3px 8px', borderRadius: '999px', border: '1px solid #fed7aa', background: '#fff7ed', color: '#c2410c', fontSize: '10px', fontWeight: 700 }}>
                  {missingAmountCount} sans montant
                </span>
              )}
              {hasActiveMerchantFilters && (
                <button
                  onClick={() => setMerchantFilters(DEFAULT_FILTERS)}
                  style={{ padding: '3px 8px', borderRadius: '999px', border: '1px solid #fecdd3', background: '#fff1f2', color: '#be123c', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}
                >
                  <X size={9} style={{ display: 'inline', marginRight: '3px', verticalAlign: 'middle' }} />
                  Effacer
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {showFilterSheet && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', background: 'rgba(10,18,40,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowFilterSheet(false)}
        >
          <div
            style={{ background: 'white', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '520px', margin: '0 auto', boxShadow: '0 -24px 60px rgba(0,0,0,0.18)', paddingBottom: 'env(safe-area-inset-bottom)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '14px 0 0', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e5e7eb' }} />
            </div>
            <div style={{ padding: '16px 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>Filtrer par période</div>
              <button
                onClick={() => setShowFilterSheet(false)}
                style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={14} color="#9ca3af" />
              </button>
            </div>
            <div style={{ padding: '8px 20px 28px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { id: 'all', label: 'Toutes les commandes', sub: 'Pas de filtre de date' },
                { id: 'today', label: "Aujourd'hui", sub: 'Commandes du jour' },
                { id: 'week', label: 'Cette semaine', sub: 'Depuis lundi' },
                { id: 'month', label: 'Ce mois', sub: 'Depuis le 1er' },
              ].map((period) => {
                const isActive = (merchantFilters.period || 'all') === period.id;
                return (
                  <button
                    key={period.id}
                    onClick={() => {
                      setMerchantFilters((filters) => ({ ...filters, period: period.id, customFrom: '', customTo: '' }));
                      setShowFilterSheet(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: '14px', border: `1.5px solid ${isActive ? '#173b78' : '#eef0f6'}`, background: isActive ? '#f0f4ff' : '#f8fafc', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                  >
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: isActive ? '#173b78' : '#1e293b' }}>{period.label}</div>
                      <div style={{ fontSize: '11px', color: isActive ? '#2a66b8' : '#94a3b8', marginTop: '2px' }}>{period.sub}</div>
                    </div>
                    {isActive && <Check size={16} color="#173b78" strokeWidth={2.5} />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[0, 1, 2, 3].map((index) => <SkeletonRow key={index} />)}
        </div>
      ) : deliveries.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '20px', padding: '48px 24px', textAlign: 'center', border: '1px solid #eef0f6', boxShadow: '0 2px 12px rgba(19,40,72,0.06)' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '24px', background: 'linear-gradient(135deg, #f0f4ff, #dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Package size={36} color="#173b78" />
          </div>
          <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Lance ton business</div>
          <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '28px', lineHeight: 1.6 }}>
            Crée ta première commande
            <br />
            et suis-la en temps réel
          </div>
          <button
            onClick={onCreateOrder}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 28px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #c53a2d, #8b1e16)', color: 'white', fontSize: '14px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 20px rgba(197,58,45,0.35)' }}
          >
            <Plus size={16} /> Créer une commande
          </button>
        </div>
      ) : filteredMerchantOrders.length === 0 ? (
        <EmptyStateCard
          image="/images/stat-commandes-a-traiter.webp"
          imageAlt="Aucune commande"
          title="Aucune commande trouvée"
          description="Modifie ou réinitialise les filtres pour revoir toutes les commandes."
          actionLabel="Réinitialiser les filtres"
          onAction={() => setMerchantFilters(DEFAULT_FILTERS)}
          tone="info"
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredMerchantOrders.map((delivery) => {
            const isSelected = selectedId === delivery.id;
            const qty = Math.max(1, Number(delivery.quantity) || 1);
            return (
              <div
                key={delivery.id}
                onClick={() => onSelectOrder(isSelected ? null : delivery)}
                style={{
                  background: 'white',
                  borderRadius: '14px',
                  border: `1.5px solid ${isSelected ? '#173b78' : '#eef0f6'}`,
                  boxShadow: isSelected ? '0 4px 16px rgba(23,59,120,0.1)' : '0 2px 6px rgba(19,40,72,0.05)',
                  display: 'flex',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  position: 'relative',
                }}
              >
                <div style={{ width: '4px', background: orderBarColor(delivery), flexShrink: 0, borderRadius: '13px 0 0 13px' }} />
                <div style={{ flex: 1, padding: '13px 14px', minWidth: 0, display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    {delivery.order_number && (
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#173b78', fontFamily: 'monospace', background: '#eff6ff', padding: '1px 6px', borderRadius: '5px', flexShrink: 0 }}>
                        #{delivery.order_number}
                      </span>
                    )}
                    <span style={{ fontWeight: 700, fontSize: '14px', color: delivery.client ? '#0f172a' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      {delivery.client || '—'}
                    </span>
                  </div>
                  {delivery.address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#94a3b8', marginBottom: '3px', overflow: 'hidden' }}>
                      <MapPin size={10} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{delivery.address}</span>
                    </div>
                  )}
                  {delivery.product_name && (
                    <div style={{ fontSize: '11px', color: '#2a66b8', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>
                      {delivery.product_name}
                      {qty > 1 ? ` ×${qty}` : ''}
                    </div>
                  )}
                  {confTab === 'recall' && delivery.recall_note && (
                    <div style={{ fontSize: '11px', color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '7px', padding: '5px 8px', marginBottom: '4px' }}>
                      {delivery.recall_note}
                    </div>
                  )}
                  {/* Le marchand voit combien de fois son client a été tenté, et quand */}
                  {confTab === 'recall' && (() => {
                    const n = Number(delivery.contact_attempts_count) || 0;
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); onOpenHistory(delivery); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', padding: '6px 8px', marginBottom: '4px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <History size={11} color="#64748b" style={{ flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: '10px', color: '#475569', fontWeight: 600 }}>
                          {n === 0
                            ? 'Aucun appel enregistré'
                            : `${n} appel${n > 1 ? 's' : ''} · dernier ${formatAttemptTime(delivery.last_contact_at)}`}
                        </span>
                        <span style={{ fontSize: '10px', color: '#173b78', fontWeight: 700 }}>Voir ›</span>
                      </button>
                    );
                  })()}
                  {/* La pastille ne dit quelque chose que dans l'onglet Confirmé,
                      où le statut varie vraiment : livré, en transit ou échec.
                      Ailleurs elle répète l'onglet sélectionné. */}
                  {confTab === 'confirmed' && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', marginTop: '4px', padding: '2px 7px', borderRadius: '6px', background: (statusConfig[delivery.status] || statusConfig.pending).bg, border: `1px solid ${(statusConfig[delivery.status] || statusConfig.pending).border}`, fontSize: '10px', fontWeight: 700, color: (statusConfig[delivery.status] || statusConfig.pending).color }}>
                      {(statusConfig[delivery.status] || statusConfig.pending).label}
                    </div>
                  )}
                </div>

                {/* Même colonne que les cartes admin : montant en haut à droite,
                    l'heure dessous, le chevron pour finir. */}
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: delivery.amount ? '#173b78' : '#d1d5db', marginBottom: '2px', whiteSpace: 'nowrap' }}>
                    {delivery.amount ? `${Number(delivery.amount).toLocaleString('fr-FR')} F` : '—'}
                  </div>
                  {delivery.created_at && (
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                      {formatOrderDate(delivery.created_at)}
                    </div>
                  )}
                  <ChevronRight size={15} color="#c7d2e0" />
                </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
